import {
  BrowserTracing,
  init,
  makeFetchTransport,
  makeMultiplexedTransport,
  moduleMetadataIntegration,
  replayIntegration,
  setTag,
  setUser,
} from '@sentry/vue';
import sessionManager from '../auth/session/sessionManager';
import {
  DEFAULT_ACCOUNT_ID,
  DEFAULT_ENDPOINT,
  DEFAULT_ENVIRONMENT,
  DEFAULT_USER_ID,
  DEFAULT_VERSION,
  ENDPOINTS,
  EXTRA_KEY,
} from './consts';
import { getLogger } from '../logger/logger';

const logger = getLogger('Infra');

class SentryInitializer {
  constructor(
    vueApps,
    dsn,
    sampleRate,
    tracesSampleRate,
    tracePropagationTargets,
    blockedAccounts,
  ) {
    try {
      if (!vueApps.length) {
        logger.error('Requires at least one Vue App');
      }
      if (window.leConfiguration && window.leConfiguration.lp_grid
        && window.leConfiguration.lp_phase) {
        this.environment = `${window.leConfiguration.lp_grid}-${window.leConfiguration.lp_phase}`;
        this.version = window.leResourceBaseUrl.split('/').filter(Boolean).pop();
      } else {
        logger.error('window.leConfiguration is not defined');
        this.environment = DEFAULT_ENVIRONMENT;
        this.version = DEFAULT_VERSION;
      }
    } catch (error) {
      logger.error('Error initializing Sentry configuration:', error);
      this.environment = DEFAULT_ENVIRONMENT;
      this.version = DEFAULT_VERSION;
    }
    this.tracePropagationTargets = tracePropagationTargets || [];
    this.EXTRA_KEY = EXTRA_KEY;
    this.apps = vueApps;
    this.dsn = dsn;
    this.sampleRate = sampleRate || 0;
    this.tracesSampleRate = tracesSampleRate || 0;
    this.blockedAccounts = blockedAccounts || [];
  }

  initialize() {
    try {
      const userId = sessionManager.getUserId() || DEFAULT_USER_ID;
      const accountId = sessionManager.getAccountId() || DEFAULT_ACCOUNT_ID;
      const disableSampling = this.shouldDisableSamplingForBlockedAccounts(accountId);
      const sampleRate = disableSampling ? 0 : this.sampleRate;
      const tracesSampleRate = disableSampling ? 0 : this.tracesSampleRate;

      init({
        app: this.apps,
        transport: this.createMultiplexedTransport(),
        environment: this.environment,
        release: this.version,
        dsn: this.dsn,
        integrations: [

          /* eslint-disable no-restricted-globals */
          new BrowserTracing({
            beforeNavigate: (context) => {
              const endpoint = this.determineApp();
              setTag('location', endpoint);
              setTag('accountId', accountId);
              setTag('userId', userId);
              return {
                ...context,
                name: location.hash,
              };
            },
          }),
          moduleMetadataIntegration(),
          replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ],
        beforeSend: (event) => this.beforeSendFunc(event),
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
        sampleRate,
        trackComponents: true,
        tracesSampleRate,
        tracePropagationTargets: this.tracePropagationTargets,
      });
      setUser({
        id: userId,
        accountId,
      });
    } catch (error) {
      logger.error('Error during Sentry initialization:', error);
    }
  }

  shouldDisableSamplingForBlockedAccounts(accountId) {
    return !!(this.blockedAccounts.includes(accountId) || window.isAutomated);
  }

  beforeSendFunc(event) {
    try {
      if (event && event.exception && event.exception.values[0]
        && event.exception.values[0].stacktrace
        && event.exception.values[0].stacktrace.frames) {
        const { frames } = event.exception.values[0].stacktrace;
        const routeTo = frames
          .filter((frame) => frame.module_metadata && frame.module_metadata.dsn)
          .map((v) => v.module_metadata);

        if (routeTo.length) {
          // eslint-disable-next-line no-param-reassign
          event.extra = {
            ...event.extra,
            [this.EXTRA_KEY]: routeTo,
          };
        }
      }
      return event;
    } catch (error) {
      logger.error('Error in beforeSend:', error);
    }
    return event;
  }

  /* eslint-disable class-methods-use-this */
  determineApp() {
    try {
      const endpoint = location.hash;
      const keys = Object.keys(ENDPOINTS);
      const matchedKey = keys.find((key) => endpoint.includes(key));
      return matchedKey ? ENDPOINTS[matchedKey] : DEFAULT_ENDPOINT;
    } catch (error) {
      logger.error('Error determining app:', error);
      return DEFAULT_ENDPOINT;
    }
  }

  createMultiplexedTransport() {
    return makeMultiplexedTransport(makeFetchTransport, (args) => {
      try {
        const event = args.getEvent();
        if (event && event.extra && Array.isArray(event.extra[this.EXTRA_KEY])) {
          return event.extra[this.EXTRA_KEY];
        }
      } catch (error) {
        logger.error('Error in makeMultiplexedTransport callback:', error);
      }
      return [];
    });
  }
}

export default SentryInitializer;
